/**
 * Creates a debounced version of an asynchronous function that returns a Promise.
 *
 * The debounced function delays invoking the provided callback until after a specified
 * amount of time has elapsed since the last time it was invoked. This is useful for
 * implementing behavior that should only happen after a repeated action has completed.
 *
 * Unlike regular debounce, this implementation returns a Promise that resolves with the
 * callback's return value or rejects if the callback throws an error.
 *
 * @param {Function} callback - The function to debounce. Can be async or regular.
 * @param {number} delay - The number of milliseconds to delay execution.
 * @returns {Function} A debounced function that returns a Promise.
 *
 * @example
 * // Basic usage with a regular function
 * const debouncedSearch = asyncDebounce((query) => {
 *   return searchAPI(query);
 * }, 300);
 *
 * // Usage with async/await
 * async function handleInput(e) {
 *   try {
 *     const results = await debouncedSearch(e.target.value);
 *     updateUI(results);
 *   } catch (err) {
 *     handleError(err);
 *   }
 * }
 *
 * @example
 * // Use case: Form validation during typing
 * const debouncedValidate = asyncDebounce(async (value) => {
 *   const isValid = await validateWithServer(value);
 *   return isValid;
 * }, 500);
 *
 * // Use case: Autocomplete suggestions
 * const debouncedFetchSuggestions = asyncDebounce(async (input) => {
 *   const suggestions = await fetchSuggestions(input);
 *   return suggestions;
 * }, 250);
 */
function asyncDebounce(callback, delay) {
  let timer;

  return (...args) =>
    new Promise((resolve, reject) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        try {
          const output = callback(...args);
          resolve(output);
        } catch (err) {
          reject(err);
        }
      }, delay);
    });
}

export default asyncDebounce;
